import * as React from 'react'
import { FaIcon, faPaletteSolid, faCaretDownSolid } from '@/lib/fa'
import { useDayTypePaletteStore } from '@/modules/drivers/stores/dayTypePaletteStore'
import {
  DAY_TYPE_PALETTE_OPTIONS,
  type DayTypePaletteId,
} from './charts/yearly/palettes/dayTypePalettes'

const PREVIEW_LABELS = ['W', 'U', 'C', 'Nn', 'P']


const YearlyPaletteSwitch = () => {
  const paletteId = useDayTypePaletteStore((s) => s.paletteId)
  const setPaletteId = useDayTypePaletteStore((s) => s.setPaletteId)
  const [isOpen, setIsOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    if (isOpen) {
      document.addEventListener('mousedown', onPointerDown)
      document.addEventListener('keydown', onKeyDown)
    }
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  const activeOption =
    DAY_TYPE_PALETTE_OPTIONS.find((o) => o.id === paletteId) ??
    DAY_TYPE_PALETTE_OPTIONS[0]

  const select = (id: DayTypePaletteId) => {
    setPaletteId(id)
    setIsOpen(false)
  }

  return (
    <div className="tw-relative" ref={containerRef}>
      <div
        role="button"
        title={`Paleta kolorów: ${activeOption.label}`}
        onClick={() => setIsOpen((v) => !v)}
        className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2 tw-py-1 ${
          isOpen ? 'tw-bg-primary/15' : 'hover:tw-bg-primary/10'
        }`}
      >
        <FaIcon icon={faPaletteSolid} className="tw-text-primary" />
        <div className="tw-flex tw-items-center tw-gap-[2px]">
          {activeOption.swatches.map((visual, idx) => (
            <span
              key={idx}
              className="tw-h-3 tw-w-2.5"
              style={{
                background: visual.background,
                borderLeft: `2px solid ${visual.border}`,
              }}
            />
          ))}
        </div>
        <FaIcon
          icon={faCaretDownSolid}
          className={`tw-h-3 tw-w-3 tw-text-primary tw-transition-transform ${
            isOpen ? 'tw-rotate-180' : ''
          }`}
        />
      </div>

      {isOpen && (
        <div className="tw-absolute tw-left-0 tw-top-full tw-z-[200] tw-mt-2 tw-w-[270px] tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-100 tw-p-2 tw-shadow-2xl">
          <p className="tw-m-0 tw-px-2 tw-pb-1 tw-pt-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral">
            Paleta kolorów rodzajów dnia
          </p>
          {DAY_TYPE_PALETTE_OPTIONS.map((option) => {
            const isActive = option.id === paletteId
            return (
              <div
                key={option.id}
                role="button"
                onClick={() => select(option.id)}
                className={`tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3 tw-rounded-md tw-px-2 tw-py-2 ${
                  isActive ? 'tw-bg-primary/10' : 'hover:tw-bg-primary/5'
                }`}
              >
                <span
                  className={`tw-text-sm ${
                    isActive
                      ? 'tw-font-semibold tw-text-primary'
                      : 'tw-font-medium tw-text-content-base'
                  }`}
                >
                  {option.label}
                </span>
                <div className="tw-flex tw-items-center tw-gap-[3px]">
                  {option.swatches.map((visual, idx) => (
                    <span
                      key={idx}
                      className="tw-grid tw-h-[18px] tw-w-7 tw-place-items-center tw-text-[10px] tw-font-semibold"
                      style={{
                        background: visual.background,
                        color: visual.text,
                        borderLeft: `${visual.borderWidth}px solid ${visual.border}`,
                      }}
                    >
                      {PREVIEW_LABELS[idx]}
                    </span>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default YearlyPaletteSwitch
